import { useSuspenseQuery } from '@apollo/client';
import { SortableList } from '../../components/SortableList/SortableList';
import { graphql } from '../../generated';
import { t } from '../../locales/translate';
import { withSuspendedCard } from '../../util/suspense';
import { FilterBar } from '../../components/FilterBar/FilterBar';
import { Stack } from '../../components/Stack/Stack';
import React, { useState } from 'react';
import { StatusPill } from '../../components/StatusPill/StatusPill';
import { shopPath, voucherCampaignPath } from '../../routes';
import { formatDateTime, formatCurrency, formatPercentage, formatNumber } from '../../util/formatters';
import { Card } from '../../components/Card/Card';
import { Text } from '../../components/text/text';
import { Icon } from '../../components/Icon/Icon';
import { useModalPresenter } from '../../components/Modal/PresentedModal';
import { VoucherCampaignCreate } from './create';
import { useQueryAsState } from '../../model/history';
import { Button } from '../../components/Button/Button';
import { FormSelect } from '../../components/Form/Select/FormSelect';
import { VoucherCampaign, VoucherCampaignStatus } from '../../generated/graphql';
import { getVoucherCampaignStatus } from './util';
import { NavLink } from 'react-router-dom';

const VOUCHER_CAMPAIGNS = graphql(`
  query VoucherCampaigns($cursor: String, $first: Int, $terms: [String!], $status: VoucherCampaignStatus) {
    voucherCampaigns(after: $cursor, first: $first, terms: $terms, status: $status) {
      edges {
        node {
          id
          title
          internalTitle
          createdAt
          discountType
          amount
          voucherCodes(first: 2) {
            nodes {
              id
              code
            }
          }
          voucherCodesUsedCount
          voucherCodesCount
          status
          target
          shop {
            name
            slug
          }
          automations {
            nodes {
              id
              operation
              executeAt
            }
            permissions {
              id
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`);

function CampaignsList$(): JSX.Element {
  const [params] = useQueryAsState();
  const [statusFilter, setStatusFilter] = useState<VoucherCampaignStatus>();

  const { data, fetchMore } = useSuspenseQuery(VOUCHER_CAMPAIGNS, {
    variables: {
      terms: params.query?.map((item) => `${item.exclude ? '-' : ''}${item.label}`),
      status: statusFilter || undefined,
      first: 10,
    },
  });

  const modal = useModalPresenter();

  const voucherCampaigns = data.voucherCampaigns.edges.map((edge) => edge.node);

  const pageInfo = data.voucherCampaigns.pageInfo;

  return (
    <Card
      title={t.page.voucherCampaigns.voucherCampaigns}
      actions={<Icon name="plus" onClick={() => modal.present(VoucherCampaignCreate, {})} />}
      fullbleed
      footer={
        pageInfo?.hasNextPage && (
          <Button
            title={t.actions.loadMore}
            onClick={() =>
              fetchMore({
                variables: {
                  cursor: pageInfo.endCursor,
                },
              })
            }
          />
        )
      }
    >
      <SortableList
        items={voucherCampaigns}
        defaultSort={{
          sortBy: 'createdAt',
          sortAscending: false,
        }}
        actions={
          <Stack line align="start">
            <FilterBar filters={['query']} queryStyle={{ minWidth: '250px' }} />
            <FormSelect
              label={t.page.voucherCampaigns.status}
              allowEmpty
              widthAuto
              style={{
                minWidth: '200px',
              }}
              options={[
                { label: t.model.status.active, value: 'ACTIVE' },
                { label: t.model.status.inactive, value: 'INACTIVE' },
              ]}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value as VoucherCampaignStatus)}
              required
              icon="activity"
            />
          </Stack>
        }
        rowLink={(row) => voucherCampaignPath(row.id)}
        columns={[
          {
            label: t.page.voucherCampaigns.title,
            dataKey: 'internalTitle',
            cellRenderer: (row) => row.internalTitle || row.title,
          },
          {
            label: t.model.shop._name,
            dataKey: 'shop',
            disableLink: true,
            cellRenderer: ({ shop }) => (
              <NavLink className="button button--link" to={shopPath(shop.slug)}>
                {shop.name}
              </NavLink>
            ),
          },
          {
            label: t.page.voucherCampaigns.createdOn,
            dataKey: 'createdAt',
            cellRenderer: ({ createdAt }) => formatDateTime(createdAt),
          },
          {
            label: t.page.voucherCampaigns.voucherType,
            dataKey: 'discountType',
            cellRenderer: ({ discountType, amount }) => {
              return <div>{discountType === 'ABSOLUTE' ? formatCurrency(amount) : formatPercentage(amount)}</div>;
            },
          },
          {
            label: t.page.voucherCampaigns.codes,
            dataKey: 'voucherCodes',
            cellRenderer: ({ voucherCodes, voucherCodesCount }) => {
              return (
                <Stack size="zero">
                  {voucherCodes.nodes.map(({ code }) => code).join(', ')}
                  {voucherCodesCount > 2 && '...'}
                </Stack>
              );
            },
          },
          {
            label: t.page.voucherCampaigns.usageCodes,
            dataKey: 'voucherCodesCount',
            cellRenderer: ({ voucherCodesCount, voucherCodesUsedCount }) => {
              return [
                formatNumber(voucherCodesUsedCount, 0),
                voucherCodesCount ? formatNumber(voucherCodesCount, 0) : null,
              ]
                .filter((value) => value !== null)
                .join('/');
            },
          },
          {
            label: t.page.voucherCampaigns.status,
            dataKey: 'status',
            cellRenderer: (row) => {
              return <StatusPill status={getVoucherCampaignStatus(row as VoucherCampaign)} />;
            },
          },
          {
            label: t.page.voucherCampaigns.automations,
            dataKey: 'automations',
            cellRenderer: ({ automations }) => (
              <Stack size="zero">
                {automations.nodes.length === 0 && <Text type="breadcrumb">-</Text>}
                {automations.nodes.map(({ operation, executeAt }) => {
                  return (
                    <div key={operation}>
                      {t.page.voucherCampaigns[operation.toLowerCase()]}: {formatDateTime(executeAt)}
                    </div>
                  );
                })}
              </Stack>
            ),
          },
        ]}
      />
    </Card>
  );
}

export const CampaignsList = withSuspendedCard(CampaignsList$, {
  loadingTitle: t.page.voucherCampaigns.voucherCampaigns,
});
